import { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { sortByLabel, sortByName, sortStrings } from "@/lib/sortRows";
import AppLayout from "@/components/AppLayout";
import { StatusBadge, PriorityBadge, STATUS_STYLES, PRIORITY_STYLES } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import MultiSelect from "@/components/MultiSelect";
import PagedList from "@/components/PagedList";
import AvatarStack from "@/components/AvatarStack";
import PeopleMultiSelect from "@/components/PeopleMultiSelect";
import RequesterPicker from "@/components/RequesterPicker";
import { ArrowLeftIcon, PaperPlaneTiltIcon, XIcon, PlusIcon, ArrowSquareOutIcon, WarningCircleIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import { prettyUrl } from "@/lib/prettyUrl";
import ReportIssueModal from "@/components/ReportIssueModal";
import LiveApprovalBanner from "@/components/LiveApprovalBanner";

// The task_status enum, exactly: Open -> WIP -> Test -> UAT -> Cutover -> Live,
// with Hold as a side-state at any point. Live is the terminal "shipped" state.
// "closed" is the terminal administrative state — finished with, as opposed to
// shipped — and was added to the enum itself by
// supabase/schema_task_status_closed.sql. It is NOT a frontend-only label: the
// Postgres task_status type has to carry it, or every status change to Closed
// comes back as "invalid input value for enum task_status" behind a generic
// "something went wrong" toast (which is exactly what happened when this list
// offered Closed before the enum had it). Keep this list, the enum, and the
// ai-chat edge function's list_tasks status enum in step.
const T_STATUSES = ["open", "wip", "test", "uat", "cutover", "live", "closed", "cancelled", "hold"];
const T_PRIORITIES = ["low", "medium", "high", "urgent"];

// timestamptz -> yyyy-mm-dd for a plain <input type="date">
function toDateInput(v) {
  if (!v) return "";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
}

// Turns one task_activity row into a human sentence fragment — the caller
// prefixes it with the actor's name. from_name/to_name are only populated
// server-side (api.js) when from_value/to_value is a user id; everything
// else (status/priority/due date/title) stays a raw column value here and
// gets its display label looked up locally.
function describeActivity(a) {
  const statusLabel = (v) => STATUS_STYLES[v]?.label || v;
  const priorityLabel = (v) => PRIORITY_STYLES[v]?.label || v;
  switch (a.action) {
    case "created":
      return "created this task";
    case "status_changed":
      return `changed status from ${statusLabel(a.from_value)} to ${statusLabel(a.to_value)}`;
    case "priority_changed":
      return `changed priority from ${priorityLabel(a.from_value)} to ${priorityLabel(a.to_value)}`;
    case "primary_assignee_changed":
      if (!a.from_value) return `assigned this task to ${a.to_name || "someone"}`;
      if (!a.to_value) return `unassigned this task (was ${a.from_name || "someone"})`;
      return `reassigned this task from ${a.from_name || "someone"} to ${a.to_name || "someone"}`;
    case "assignee_added":
      return `added ${a.to_name || "a teammate"} as an assignee`;
    case "assignee_removed":
      return `removed ${a.from_name || "a teammate"} as an assignee`;
    case "due_date_changed":
      if (!a.from_value) return `set the due date to ${a.to_value}`;
      if (!a.to_value) return `cleared the due date (was ${a.from_value})`;
      return `changed due date from ${a.from_value} to ${a.to_value}`;
    case "eta_changed":
      if (!a.from_value) return `set the ETA to ${a.to_value}`;
      if (!a.to_value) return `cleared the ETA (was ${a.from_value})`;
      return `changed ETA from ${a.from_value} to ${a.to_value}`;
    case "revised_eta_changed":
      // The REASON travels on the row (task_activity.note, written by
      // log_task_updated at the moment of the change) and is rendered on its
      // own line under this sentence — see the timeline below. It is not
      // appended here because a reason is a sentence of its own, often long
      // enough to bury the dates it explains.
      if (!a.from_value) return `set a revised ETA of ${a.to_value}`;
      if (!a.to_value) return `cleared the revised ETA (was ${a.from_value})`;
      return `changed revised ETA from ${a.from_value} to ${a.to_value}`;
    case "attachment_url_changed":
      if (!a.from_value) return "added an attachment link";
      if (!a.to_value) return "removed the attachment link";
      return "changed the attachment link";
    case "requested_by_changed":
      if (!a.from_value) return `recorded ${a.to_value} as the requester`;
      if (!a.to_value) return `cleared the requester (was ${a.from_value})`;
      return `changed the requester from ${a.from_value} to ${a.to_value}`;
    case "title_changed":
      return `renamed this task from "${a.from_value}" to "${a.to_value}"`;
    case "description_changed":
      return "updated the description";
    case "live_approval_requested":
      return "asked for approval to go Live";
    case "live_approval_approved":
      return "approved this task going Live";
    case "live_approval_rejected":
      return "rejected the request to go Live";
    default:
      return a.action;
  }
}

export default function TaskDetailPage() {
  const { id } = useParams();
  const location = useLocation();
  const { user: currentUser } = useAuth();

  /*
    Where "back" goes.

    It was hardcoded to /tasks, so opening a task from inside a project and
    coming back dumped the reader on the global grid of every task in the
    company — they lost the project they were working in and had to filter
    their way back to it.

    The opener says where it came from, in router state. That state lives in
    the History API entry, so it survives a page refresh and the browser's own
    back button; only a pasted URL arrives without it, and that falls back to
    the global Tasks grid, which is the right answer when nothing is known.

    Carried forward across task-to-task moves (a parent link, a subtask), so
    walking down a subtask tree and pressing back still returns to the project
    rather than to whichever task happened to be one hop up.
  */
  const backTo = location.state?.from?.pathname
    ? { pathname: location.state.from.pathname, label: location.state.from.label || "Back" }
    : { pathname: "/tasks", label: "Tasks" };
  const [task, setTask] = useState(null);
  const [project, setProject] = useState(null);
  const [issueOpen, setIssueOpen] = useState(false);
  const [users, setUsers] = useState([]);
  // The task this one sits under, and the ones sitting under it. Both are plain
  // tasks — a subtask has its own status, assignees, ETA and history.
  const [members, setMembers] = useState([]);
  const [parentTask, setParentTask] = useState(null);
  const [subtasks, setSubtasks] = useState([]);
  const [subtaskOpen, setSubtaskOpen] = useState(false);
  // The same fields the New Task dialog collects, so adding a subtask is the
  // same act as adding a task — it just arrives with a parent.
  const emptySubtask = () => ({
    title: "", description: "", status: "open", priority: "medium",
    assignee_ids: [], attachment_url: "", eta: "", revised_eta: "",
    // A subtask is a full task, so the database requires these of it too
    // (trg_require_task_creation_fields fires on every insert into tasks).
    requested_by: "",
  });
  const [subtaskForm, setSubtaskForm] = useState(emptySubtask());
  const [subtaskSaving, setSubtaskSaving] = useState(false);
  // Tab 2: attaching tasks that already exist, rather than writing a new one.
  const [projectTasks, setProjectTasks] = useState([]);
  const [attachIds, setAttachIds] = useState([]);
  const [attaching, setAttaching] = useState(false);
  const [form, setForm] = useState(null);
  const [comments, setComments] = useState([]);
  const [activity, setActivity] = useState([]);
  const [subtaskActivity, setSubtaskActivity] = useState([]);
  const [commentBody, setCommentBody] = useState("");
  const [posting, setPosting] = useState(false);
  const [addAssigneeId, setAddAssigneeId] = useState("");
  const [loading, setLoading] = useState(true);

  const isAdmin = currentUser?.role === "admin";
  // "owner" has identical permissions to "manager" everywhere — see
  // is_manager_or_admin() in schema_owner_role.sql.
  const isManager = currentUser?.role === "manager" || currentUser?.role === "owner";

  /*
    Who may edit this task.

    Admin and manager as before, plus anyone ON THIS TASK'S PROJECT — its owner
    or someone on its Members roster. Membership of this project is what grants
    it, not membership of anything anywhere.

    This mirrors the RLS rule exactly (supabase/schema_project_member_task_edit.sql):
    if the UI offered controls the policies then refused, every save would fail
    with a generic error, which is worse than not offering them.
  */
  const memberIdSet = new Set(members.map((m) => m.user_id));
  const isProjectPerson = Boolean(
    currentUser?.id && (project?.owner_id === currentUser.id || memberIdSet.has(currentUser.id)),
  );
  const canManage = isAdmin || isManager || isProjectPerson;

  // An ETA exists, so it is now the baseline and cannot move — for everyone,
  // including the people this page just widened edit rights to.
  const etaLocked = Boolean(task?.eta);

  /*
    Is this save moving the revised ETA?

    Compared against the STORED date rather than tracked with a flag, so
    typing a new date and then putting the original one back counts as no
    change — and does not demand a reason for a revision that never happened.
    Both sides are yyyy-mm-dd strings, which is also what the database compares
    (::date), so the two agree about what "changed" means.
  */
  const revisedEtaChanged = Boolean(
    form && task && form.revised_eta !== toDateInput(task.revised_eta),
  );

  const load = async () => {
    try {
      const { data: t } = await api.get(`/tasks/${id}`);
      const [{ data: p }, { data: u }, { data: c }, { data: a }, { data: kids }, { data: mem }, { data: sibs }, { data: subAct }] = await Promise.all([
        api.get(`/projects/${t.project_id}`),
        api.get("/users"),
        api.get(`/tasks/${id}/comments`),
        api.get(`/tasks/${id}/activity`),
        // Subtasks. Tolerated failure: the parent_task_id column only exists
        // once supabase/schema_task_subtasks.sql has been applied, and the rest
        // of this page must not go blank before then.
        api.get(`/tasks?parent_task_id=${id}`).catch(() => ({ data: [] })),
        api.get(`/projects/${t.project_id}/members`).catch(() => ({ data: [] })),
        // Candidates for "attach an existing task". Same project only: the
        // trigger moves a subtask into its parent's project, so offering a task
        // from elsewhere would silently relocate it.
        api.get(`/tasks?project_id=${t.project_id}`).catch(() => ({ data: [] })),
        api.get(`/tasks/${id}/subtask-activity`).catch(() => ({ data: [] })),
      ]);
      setSubtasks(kids || []);
      setMembers(mem || []);
      setProjectTasks(sibs || []);
      if (t.parent_task_id) {
        const { data: par } = await api.get(`/tasks/${t.parent_task_id}`).catch(() => ({ data: null }));
        setParentTask(par);
      } else {
        setParentTask(null);
      }
      setTask(t);
      setProject(p);
      setUsers(sortByName(u));
      setComments(c);
      setActivity(a);
      setSubtaskActivity(subAct || []);
      setForm({
        title: t.title,
        description: t.description || "",
        priority: t.priority || "medium",
        due_date: toDateInput(t.due_date),
        eta: toDateInput(t.eta),
        revised_eta: toDateInput(t.revised_eta),
        // Deliberately NOT prefilled from t.revised_eta_reason. The stored
        // reason explains the date that is already there; a new date needs its
        // own, typed now. Prefilling would let the last revision's reason be
        // waved through as this one's.
        revised_eta_reason: "",
        requested_by: t.requested_by || "",
        attachment_url: t.attachment_url || "",
      });
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [id]);

  // Mirrors the "update own or manager tasks" RLS policy — a plain member
  // can only change status on a task they're personally assigned to (any
  // of the task's assignees, not just the primary).
  const canEditTask = task ? (isAdmin || isManager || (task.assignee_ids || [task.assignee_id]).includes(currentUser?.id)) : false;

  const uMap = Object.fromEntries(users.map((u) => [u._id, u.name || u.email]));
  const uById = Object.fromEntries(users.map((u) => [u._id, { id: u._id, name: u.name || u.email, avatar_url: u.avatar_url }]));

  // A subtask can only be handed to someone who can actually open it: the
  // project's owner plus its Members roster, which is exactly what the project
  // RLS policies admit. Offering the whole directory here would produce
  // subtasks assigned to people who then get a "not found" page.
  const assignablePeople = users.filter((u) => u._id === project?.owner_id || memberIdSet.has(u._id));
  const assigneeIds = task ? (task.assignee_ids || [task.assignee_id]).filter(Boolean) : [];

  // Comments and lifecycle events merged into one chronological feed —
  // matches how the request framed it ("life cycle of the task ... like a
  // comment history"), so a reviewer sees status/assignee/priority changes
  // interleaved with the discussion instead of hunting two separate lists.
  const timeline = [
    ...comments.map((c) => ({ kind: "comment", at: c.created_at, item: c })),
    ...activity.map((a) => ({ kind: "activity", at: a.created_at, item: a })),
  ].sort((x, y) => new Date(x.at) - new Date(y.at));
  const extraAssigneeIds = assigneeIds.filter((uid) => uid !== task?.assignee_id);
  const addableUsers = users.filter((u) => !assigneeIds.includes(u._id));

  // Returns whether the write actually landed. It used to swallow the failure
  // and return undefined, so saveDetails announced "Task updated" over the top
  // of its own error toast — and now that the database can refuse a save
  // outright (a revised ETA with no reason, a locked ETA), claiming success
  // would be worse than unhelpful.
  const patchTask = async (patch) => {
    // Only the changed fields — task carries a derived assignee_ids array
    // that isn't a real column, spreading the whole object back would
    // send that straight to Postgres and fail.
    try {
      await api.patch(`/tasks/${id}`, patch);
      load();
      return true;
    } catch (e) {
      // The WHOLE error, not e.response.data.detail: this wraps the Supabase
      // client, whose errors have no .response at all, so the old form
      // evaluated to undefined and printed a generic message over the top of
      // the real one. The trigger messages here are the useful part.
      toast.error(formatApiErrorDetail(e));
      return false;
    }
  };

  const saveDetails = async () => {
    /*
      A revised ETA does not move without a written reason.

      Enforced in the database too (supabase/schema_revised_eta_reason.sql), so
      this check is about telling the person WHY the save was refused before
      they lose the rest of their edits to a round trip — not about being the
      rule.
    */
    const revisedReason = (form.revised_eta_reason || "").trim();
    if (revisedEtaChanged && !revisedReason) {
      toast.error("Add a reason for revising the ETA — it is recorded in this task's history.");
      return;
    }
    const ok = await patchTask({
      title: form.title,
      description: form.description,
      priority: form.priority,
      due_date: form.due_date || null,
      // Omitted entirely once locked: the column is timestamptz and this form
      // holds "YYYY-MM-DD", so re-sending it invites a spurious "changed"
      // comparison on a save that never touched the field.
      ...(task?.eta ? {} : { eta: form.eta || null }),
      revised_eta: form.revised_eta || null,
      // Only when the date moved. Sending it on every save would overwrite the
      // reason attached to a date nobody touched.
      ...(revisedEtaChanged ? { revised_eta_reason: revisedReason } : {}),
      requested_by: form.requested_by.trim() || null,
      attachment_url: form.attachment_url.trim() || null,
    });
    if (ok) toast.success("Task updated");
  };

  const setPrimaryAssignee = async (uid) => {
    await patchTask({ assignee_id: uid || null });
  };

  const addAssignee = async () => {
    if (!addAssigneeId) return;
    try {
      await api.post(`/tasks/${id}/assignees`, { user_id: addAssigneeId });
      setAddAssigneeId("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    }
  };

  const removeAssignee = async (uid) => {
    try {
      await api.delete(`/tasks/${id}/assignees/${uid}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    }
  };

  const postComment = async () => {
    if (!commentBody.trim()) return;
    setPosting(true);
    try {
      await api.post(`/tasks/${id}/comments`, { body: commentBody.trim() });
      setCommentBody("");
      const { data: c } = await api.get(`/tasks/${id}/comments`);
      setComments(c);
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setPosting(false);
    }
  };

  const createSubtask = async () => {
    if (!subtaskForm.title.trim()) { toast.error("Title required"); return; }
    // Somebody has to own it. A subtask with nobody on it is invisible in every
    // workload view and every digest, so it may as well not exist — and the
    // database refuses the insert anyway (trg_require_task_assignee).
    if (subtaskForm.assignee_ids.length === 0) {
      toast.error("Pick at least one assignee for the subtask.");
      return;
    }
    // Same two the New Task dialog requires. A subtask is a task: the insert
    // trigger does not care which dialog it came from.
    if (!subtaskForm.eta) {
      toast.error("Set an ETA for the subtask.");
      return;
    }
    if (!subtaskForm.requested_by.trim()) {
      toast.error("Say who requested this subtask.");
      return;
    }
    if (subtaskSaving) return;
    setSubtaskSaving(true);
    try {
      // project_id is sent for the pre-migration case; once the trigger exists
      // it overrides this with the parent's project anyway, since the tasks RLS
      // policies are project-based and a child must share its parent's
      // audience. The number itself is assigned by the database.
      // tasks.assignee_id holds ONE person; anyone beyond the first goes
      // through task_assignees, exactly as the New Task dialog does.
      const [primary, ...rest] = subtaskForm.assignee_ids;
      const { data: created } = await api.post("/tasks", {
        title: subtaskForm.title.trim(),
        description: subtaskForm.description,
        status: subtaskForm.status,
        priority: subtaskForm.priority,
        project_id: task.project_id,
        parent_task_id: task._id || task.id,
        assignee_id: primary || null,
        // eta/revised_eta are timestamptz: the date inputs' empty string fails
        // Postgres's date parsing outright, so each has to become a real null.
        attachment_url: subtaskForm.attachment_url.trim() || null,
        requested_by: subtaskForm.requested_by.trim(),
        eta: subtaskForm.eta || null,
        revised_eta: subtaskForm.revised_eta || null,
      });
      for (const uid of rest) {
        await api.post(`/tasks/${created._id}/assignees`, { user_id: uid });
      }
      toast.success("Subtask added");
      setSubtaskForm(emptySubtask());
      setSubtaskOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setSubtaskSaving(false);
    }
  };

  /*
    Candidates for attaching.

    Excluded: this task itself, and anything that already has a parent. That
    second rule is what makes a cycle impossible from the UI — a descendant of
    this task necessarily has a parent, so it can never appear here. The
    database refuses a cycle anyway (see renumber_task_on_reparent), but the
    list should not offer a choice that will be rejected.
  */
  const attachCandidates = projectTasks.filter(
    (t) => t._id !== id && !t.parent_task_id,
  );

  const attachSubtasks = async () => {
    if (attachIds.length === 0) { toast.error("Pick at least one task"); return; }
    if (attaching) return;
    setAttaching(true);
    try {
      // Sequential, not Promise.all: each task takes the next sibling number,
      // and the trigger derives that from max(task_seq) — firing them together
      // would have them race for the same ordinal.
      for (const tid of attachIds) {
        await api.patch(`/tasks/${tid}`, { parent_task_id: task._id || task.id });
      }
      toast.success(`${attachIds.length} task${attachIds.length > 1 ? "s" : ""} moved under this one`);
      setAttachIds([]);
      setSubtaskOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setAttaching(false);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="px-8 py-12 text-center">
          <div className="trk-uppercase-label">Loading…</div>
        </div>
      </AppLayout>
    );
  }

  if (!task) {
    return (
      <AppLayout>
        <div className="px-8 py-12 text-center">
          <div className="trk-uppercase-label mb-2">Not found</div>
          <p className="text-sm text-zinc-500">This task doesn't exist or you don't have access to it.</p>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="trk-sticky-header sticky top-0 z-20 px-8 py-4">
        <Link
          to={backTo.pathname}
          data-testid="task-back-link"
          title={`Back to ${backTo.label}`}
          className="inline-flex items-center gap-2 mb-4 px-4 py-2 rounded-md border border-[#0176D3] bg-[#EFF6FF] text-sm font-semibold text-[#0176D3] hover:bg-[#0176D3] hover:text-white transition-colors max-w-full"
        >
          <ArrowLeftIcon size={18} weight="bold" className="shrink-0" />
          <span className="truncate">Back to {backTo.label}</span>
        </Link>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="trk-uppercase-label flex items-center gap-2">
              <span>{task.parent_task_id ? "Subtask" : "Task"}</span>
              {task.task_number && (
                <span className="font-mono text-[#0176D3] normal-case tracking-normal">#{task.task_number}</span>
              )}
            </div>
            <h1 className="font-display text-3xl font-extrabold tracking-tight truncate" data-testid="task-detail-title">{task.title}</h1>
            {/* Where this sits. A subtask is meaningless without its parent —
                its number literally extends the parent's. */}
            {parentTask && (
              <Link
                to={`/tasks/${parentTask._id || parentTask.id}`}
                state={{ from: backTo }}
                data-testid="task-parent-link"
                className="text-xs text-zinc-500 hover:text-[#0176D3] mt-1 inline-flex items-center gap-1"
              >
                ↰ Subtask of {parentTask.task_number ? `#${parentTask.task_number} ` : ""}{parentTask.title}
              </Link>
            )}
          </div>
          <Button
            onClick={() => setIssueOpen(true)}
            data-testid="report-issue-task-detail"
            variant="outline"
            className="rounded-sm text-amber-700 border-amber-300 hover:bg-amber-50"
          >
            <WarningCircleIcon size={14} weight="fill" className="mr-1" /> Report Issue
          </Button>
        </div>

        <ReportIssueModal
          open={issueOpen}
          onOpenChange={setIssueOpen}
          entityType="task"
          entityId={id}
          entityLabel={task.title}
        />
        <LiveApprovalBanner taskId={id} taskStatus={task.status} onChanged={load} />
        <div className="flex items-center gap-6 mt-4 flex-wrap">
          <div className="flex items-center gap-2">
            {canEditTask ? (
              <Select value={task.status} onValueChange={(v) => patchTask({ status: v })}>
                <SelectTrigger className="rounded-sm h-7 text-xs w-[110px]" data-testid="task-detail-status"><SelectValue /></SelectTrigger>
                <SelectContent>{sortByLabel(T_STATUSES, (s) => STATUS_STYLES[s]?.label || s).map((s) => <SelectItem key={s} value={s}>{STATUS_STYLES[s]?.label || s}</SelectItem>)}</SelectContent>
              </Select>
            ) : (
              <StatusBadge status={task.status} />
            )}
            <PriorityBadge priority={task.priority} />
          </div>
          {project && (
            <div className="text-xs text-zinc-600">
              <span className="trk-uppercase-label mr-1">Project</span>
              <Link to={`/projects/${project._id}`} className="font-medium text-[#0176D3] hover:underline">{project.title}</Link>
            </div>
          )}
          <div className="text-xs text-zinc-600">
            <span className="trk-uppercase-label mr-1">Assignee(s)</span>
            <span className="font-medium text-[#0F172A]">
              {assigneeIds.length === 0 ? "Unassigned" : assigneeIds.map((uid) => uMap[uid] || "Unknown").join(", ")}
            </span>
          </div>
          {task.requested_by && (
            <div className="text-xs text-zinc-600">
              <span className="trk-uppercase-label mr-1">Requested by</span>
              <span className="font-medium text-[#0F172A]">{task.requested_by}</span>
            </div>
          )}
          {task.due_date && (
            <div className="text-xs text-zinc-600">
              <span className="trk-uppercase-label mr-1">Due</span>
              <span className="font-mono">{new Date(task.due_date).toLocaleDateString()}</span>
            </div>
          )}
          {task.eta && (
            <div className="text-xs text-zinc-600">
              <span className="trk-uppercase-label mr-1">ETA</span>
              <span className="font-mono">{new Date(task.eta).toLocaleDateString()}</span>
            </div>
          )}
          {task.revised_eta && (
            <div className="text-xs text-zinc-600">
              <span className="trk-uppercase-label mr-1">Revised ETA</span>
              <span className="font-mono">{new Date(task.revised_eta).toLocaleDateString()}</span>
            </div>
          )}
          {task.attachment_url && (
            <a
              href={task.attachment_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="task-attachment-link"
              className="text-xs text-[#0176D3] hover:underline flex items-center gap-1"
            >
              Attachment <ArrowSquareOutIcon size={12} />
            </a>
          )}
        </div>
      </div>

      <div className="px-8 py-6 trk-fade-in grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: details + assignees */}
        <div className="lg:col-span-2 space-y-6">
          <div className="border border-border rounded-sm bg-white p-5">
            <div className="trk-uppercase-label mb-3">Details</div>
            {canManage ? (
              <div className="space-y-4">
                <div>
                  <Label className="trk-uppercase-label text-[10px]">Title</Label>
                  <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className="rounded-sm mt-1" data-testid="task-detail-title-input" />
                </div>
                <div>
                  <Label className="trk-uppercase-label text-[10px]">Description</Label>
                  <Textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className="rounded-sm mt-1" rows={5} data-testid="task-detail-description-input" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="trk-uppercase-label text-[10px]">Priority</Label>
                    <Select value={form.priority} onValueChange={(v) => setForm((f) => ({ ...f, priority: v }))}>
                      <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>{sortStrings(T_PRIORITIES).map((p) => <SelectItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="trk-uppercase-label text-[10px]">Due Date</Label>
                    <Input type="date" value={form.due_date} onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))} className="rounded-sm mt-1" data-testid="task-detail-due-date-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="trk-uppercase-label text-[10px]">ETA</Label>
                    {/* Frozen once set — for everyone, admins included. The
                        original commitment is the baseline slippage is measured
                        against, so it has to stay put; a new date belongs in
                        Revised ETA. The database refuses to move it either
                        (supabase/schema_task_eta_lock.sql), because a disabled
                        input is a suggestion, not a rule. */}
                    <Input
                      type="date"
                      value={form.eta}
                      onChange={(e) => setForm((f) => ({ ...f, eta: e.target.value }))}
                      readOnly={etaLocked}
                      disabled={etaLocked}
                      title={etaLocked ? "The ETA is fixed once set — use Revised ETA" : undefined}
                      className={`rounded-sm mt-1 ${etaLocked ? "bg-zinc-100 text-zinc-500 cursor-not-allowed" : ""}`}
                      data-testid="task-detail-eta-input"
                    />
                    {etaLocked && (
                      <div className="text-[10px] font-mono text-zinc-500 mt-1 leading-tight">
                        Fixed once set. Put a new date in Revised ETA.
                      </div>
                    )}
                  </div>
                  <div>
                    <Label className="trk-uppercase-label text-[10px]">Revised ETA</Label>
                    <Input type="date" value={form.revised_eta} onChange={(e) => setForm((f) => ({ ...f, revised_eta: e.target.value }))} className="rounded-sm mt-1" data-testid="task-detail-revised-eta-input" />
                    <div className="text-[10px] font-mono text-zinc-500 mt-1 leading-tight">
                      Every change is logged in the activity feed below.
                    </div>
                    {/* The reason the CURRENT date is what it is. Shown while
                        the field is untouched, and replaced by the box below
                        the moment a new date is picked. */}
                    {!revisedEtaChanged && task.revised_eta_reason && (
                      <div className="text-[11px] text-zinc-600 mt-1.5 leading-snug" data-testid="task-detail-revised-eta-reason-current">
                        <span className="trk-uppercase-label text-[9px] mr-1">Reason</span>
                        {task.revised_eta_reason}
                      </div>
                    )}
                  </div>
                </div>
                {/* Appears only when the date has actually been changed, which
                    is the only moment a reason means anything. A box standing
                    open on every task would collect either nothing or a reason
                    for a revision that never happened. */}
                {revisedEtaChanged && (
                  <div className="border border-amber-200 bg-amber-50 rounded-sm p-3" data-testid="task-detail-revised-eta-reason-block">
                    <Label className="trk-uppercase-label text-[10px] text-amber-900">
                      Reason for revising the ETA <span className="text-red-600">*</span>
                    </Label>
                    <Textarea
                      value={form.revised_eta_reason}
                      onChange={(e) => setForm((f) => ({ ...f, revised_eta_reason: e.target.value }))}
                      rows={2}
                      autoFocus
                      placeholder="Why is the date moving? e.g. vendor delivery slipped by two weeks"
                      className="rounded-sm mt-1 bg-white"
                      data-testid="task-detail-revised-eta-reason-input"
                    />
                    <div className="text-[10px] font-mono text-amber-800 mt-1 leading-tight">
                      {task.revised_eta ? `${new Date(task.revised_eta).toLocaleDateString()} → ` : "Setting a revised ETA · "}
                      {form.revised_eta ? new Date(`${form.revised_eta}T12:00:00`).toLocaleDateString() : "cleared"}
                      {" · kept against this change in the task's history."}
                    </div>
                  </div>
                )}
                <div>
                  <Label className="trk-uppercase-label text-[10px]">Requested by</Label>
                  <div className="mt-1">
                    <RequesterPicker
                      users={assignablePeople}
                      value={form.requested_by}
                      onChange={(v) => setForm((f) => ({ ...f, requested_by: v }))}
                      testId="task-detail-requested-by"
                    />
                  </div>
                </div>
                <div>
                  <Label className="trk-uppercase-label text-[10px]">Attachment URL</Label>
                  <Input type="url" value={form.attachment_url} onChange={(e) => setForm((f) => ({ ...f, attachment_url: e.target.value }))} placeholder="https://…" className="rounded-sm mt-1" data-testid="task-detail-attachment-input" />
                </div>
                <Button onClick={saveDetails} data-testid="save-task-details" className="rounded-sm bg-[#0176D3] hover:bg-[#005FB2] text-white">Save changes</Button>
              </div>
            ) : (
              // Read-only members/assignees used to get nothing here but the
              // bare description — Priority/Due/ETA/Revised ETA/Attachment
              // were only ever visible in the compact header row above (and
              // silently vanished there if unset). They're already allowed
              // to read every one of these columns (same RLS as the edit
              // form above uses) — this was a UI gap, not a permissions one.
              <div className="space-y-4">
                <div>
                  <Label className="trk-uppercase-label text-[10px]">Description</Label>
                  <p className="text-sm text-zinc-700 whitespace-pre-wrap mt-1">{task.description || "No description provided."}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="trk-uppercase-label text-[10px]">Priority</Label>
                    <div className="mt-1"><PriorityBadge priority={task.priority} /></div>
                  </div>
                  <div>
                    <Label className="trk-uppercase-label text-[10px]">Due Date</Label>
                    <p className="text-sm text-zinc-700 font-mono mt-1">{task.due_date ? new Date(task.due_date).toLocaleDateString() : "Not set"}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="trk-uppercase-label text-[10px]">ETA</Label>
                    <p className="text-sm text-zinc-700 font-mono mt-1">{task.eta ? new Date(task.eta).toLocaleDateString() : "Not set"}</p>
                  </div>
                  <div>
                    <Label className="trk-uppercase-label text-[10px]">Revised ETA</Label>
                    <p className="text-sm text-zinc-700 font-mono mt-1">{task.revised_eta ? new Date(task.revised_eta).toLocaleDateString() : "Not set"}</p>
                    {task.revised_eta_reason && (
                      <p className="text-[11px] text-zinc-600 mt-1 leading-snug" data-testid="task-revised-eta-reason-readonly">
                        <span className="trk-uppercase-label text-[9px] mr-1">Reason</span>
                        {task.revised_eta_reason}
                      </p>
                    )}
                  </div>
                </div>
                <div>
                  <Label className="trk-uppercase-label text-[10px]">Requested by</Label>
                  <p className="text-sm text-zinc-700 mt-1">{task.requested_by || "Not recorded"}</p>
                </div>
                <div>
                  <Label className="trk-uppercase-label text-[10px]">Attachment</Label>
                  <p className="text-sm mt-1">
                    {task.attachment_url ? (
                      <a href={task.attachment_url} target="_blank" rel="noopener noreferrer" title={task.attachment_url} className="text-[#0176D3] hover:underline break-words">{prettyUrl(task.attachment_url)}</a>
                    ) : "No attachment"}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Subtasks.

              Each row links to a real task page — a subtask carries its own
              status, assignees, ETA and history, so it needs one. The number is
              assigned by the database (see supabase/schema_task_subtasks.sql)
              and extends this task's: 12 -> 12.1, 12.2.

              A parent's status is NOT rolled up from these. Closing a parent
              does not close its children, by design; deriving one from the
              other is a much larger behavioural change than numbering. */}
          <div className="border border-border rounded-sm bg-white p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="trk-uppercase-label">
                Subtasks {subtasks.length > 0 && `(${subtasks.length})`}
              </div>
              {canManage && (
                <Dialog open={subtaskOpen} onOpenChange={(o) => { setSubtaskOpen(o); if (!o) setSubtaskForm(emptySubtask()); }}>
                  <DialogTrigger asChild>
                    <Button
                      size="sm"
                      data-testid="add-subtask-button"
                      className="rounded-sm h-7 text-xs bg-[#0176D3] hover:bg-[#005FB2]"
                    >
                      <PlusIcon size={11} weight="bold" className="mr-1" /> Add subtask
                    </Button>
                  </DialogTrigger>
                  {/* Every field the New Task dialog has, in the same order and
                      the same two-column layout. A subtask is a full task; the
                      only difference is that it arrives with a parent, so
                      collecting less here would make subtasks second-class. */}
                  <DialogContent className="rounded-sm max-w-2xl max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="font-display">
                        New subtask{task.task_number ? ` under #${task.task_number}` : ""}
                      </DialogTitle>
                    </DialogHeader>
                    <Tabs defaultValue="new">
                      <TabsList className="mb-4">
                        <TabsTrigger value="new" data-testid="subtask-tab-new">Create new task</TabsTrigger>
                        <TabsTrigger value="existing" data-testid="subtask-tab-existing">
                          Use existing tasks{attachCandidates.length > 0 && ` (${attachCandidates.length})`}
                        </TabsTrigger>
                      </TabsList>

                      <TabsContent value="new" className="space-y-4">
                      <div>
                        <Label className="trk-uppercase-label mb-2 block">Title</Label>
                        <Input
                          value={subtaskForm.title}
                          onChange={(e) => setSubtaskForm({ ...subtaskForm, title: e.target.value })}
                          data-testid="subtask-title-input"
                          className="rounded-sm"
                        />
                      </div>
                      <div>
                        <Label className="trk-uppercase-label mb-2 block">Description</Label>
                        <Textarea
                          value={subtaskForm.description}
                          onChange={(e) => setSubtaskForm({ ...subtaskForm, description: e.target.value })}
                          data-testid="subtask-description-input"
                          className="rounded-sm"
                          rows={3}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="trk-uppercase-label mb-2 block">Status</Label>
                          <Select value={subtaskForm.status} onValueChange={(v) => setSubtaskForm({ ...subtaskForm, status: v })}>
                            <SelectTrigger className="rounded-sm" data-testid="subtask-status-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {sortByLabel(T_STATUSES, (st) => STATUS_STYLES[st]?.label || st).map((st) => (
                                <SelectItem key={st} value={st}>{STATUS_STYLES[st]?.label || st}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="trk-uppercase-label mb-2 block">Priority</Label>
                          <Select value={subtaskForm.priority} onValueChange={(v) => setSubtaskForm({ ...subtaskForm, priority: v })}>
                            <SelectTrigger className="rounded-sm" data-testid="subtask-priority-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {sortStrings(T_PRIORITIES).map((pr) => (
                                <SelectItem key={pr} value={pr}>{PRIORITY_STYLES[pr]?.label || pr}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="trk-uppercase-label mb-2 block">
                            ETA <span className="text-red-600">*</span>
                          </Label>
                          <Input
                            type="date"
                            value={subtaskForm.eta}
                            onChange={(e) => setSubtaskForm({ ...subtaskForm, eta: e.target.value })}
                            data-testid="subtask-eta-input"
                            className="rounded-sm"
                          />
                        </div>
                        <div>
                          <Label className="trk-uppercase-label mb-2 block">Revised ETA</Label>
                          <Input
                            type="date"
                            value={subtaskForm.revised_eta}
                            onChange={(e) => setSubtaskForm({ ...subtaskForm, revised_eta: e.target.value })}
                            data-testid="subtask-revised-eta-input"
                            className="rounded-sm"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="trk-uppercase-label mb-2 block">
                          Requested by <span className="text-red-600">*</span>
                        </Label>
                        <RequesterPicker
                          users={assignablePeople}
                          value={subtaskForm.requested_by}
                          onChange={(v) => setSubtaskForm({ ...subtaskForm, requested_by: v })}
                          testId="subtask-requested-by"
                        />
                      </div>
                      <div>
                        <Label className="trk-uppercase-label mb-2 block">Attachment URL</Label>
                        <Input
                          type="url"
                          value={subtaskForm.attachment_url}
                          onChange={(e) => setSubtaskForm({ ...subtaskForm, attachment_url: e.target.value })}
                          placeholder="https://…"
                          data-testid="subtask-attachment-input"
                          className="rounded-sm"
                        />
                      </div>
                      <div>
                        <Label className="trk-uppercase-label mb-2 block">
                          Assignees <span className="text-red-600">*</span>
                          {subtaskForm.assignee_ids.length > 0 && ` (${subtaskForm.assignee_ids.length})`}
                        </Label>
                        {/* The project's people only — project RLS admits
                            nobody else, so anyone outside this list would get a
                            "not found" page on the subtask assigned to them. */}
                        <PeopleMultiSelect
                          users={assignablePeople}
                          value={subtaskForm.assignee_ids}
                          onChange={(ids) => setSubtaskForm({ ...subtaskForm, assignee_ids: ids })}
                          triggerPlaceholder="Unassigned"
                          placeholder="Search project members…"
                          emptyMessage="Nobody is on this project yet — add people on the project's Members tab."
                          testId="subtask-assignees"
                        />
                      </div>
                      <div className="text-xs text-zinc-500">
                        It lands in <span className="font-medium">{project?.title || "this project"}</span>, numbered
                        under #{task.task_number || "?"}, and appears in the Subtasks list below.
                        {(subtaskForm.assignee_ids.length === 0 || !subtaskForm.eta || !subtaskForm.requested_by.trim()) && (
                          <span className="block text-amber-700 mt-1" data-testid="subtask-required-hint">
                            Still needed:{" "}
                            {[
                              subtaskForm.assignee_ids.length === 0 ? "an assignee" : null,
                              !subtaskForm.eta ? "an ETA" : null,
                              !subtaskForm.requested_by.trim() ? "a requester" : null,
                            ].filter(Boolean).join(", ")}.
                          </span>
                        )}
                      </div>

                      <div className="flex justify-end">
                        <Button
                          onClick={createSubtask}
                          disabled={subtaskSaving || !subtaskForm.title.trim() || subtaskForm.assignee_ids.length === 0
                            || !subtaskForm.eta || !subtaskForm.requested_by.trim()}
                          data-testid="save-subtask-button"
                          className="rounded-sm bg-[#0176D3] hover:bg-[#005FB2]"
                        >
                          {subtaskSaving ? "Adding…" : "Create subtask"}
                        </Button>
                      </div>
                      </TabsContent>

                      <TabsContent value="existing" className="space-y-3">
                        <div>
                          <Label className="trk-uppercase-label mb-2 block">
                            Tasks to move under this one {attachIds.length > 0 && `(${attachIds.length})`}
                          </Label>
                          <MultiSelect
                            items={attachCandidates.map((t) => ({
                              id: t._id,
                              label: `${t.task_number ? `#${t.task_number} ` : ""}${t.title}`,
                              hint: STATUS_STYLES[t.status]?.label || t.status,
                            }))}
                            value={attachIds}
                            onChange={setAttachIds}
                            placeholder="Search this project's tasks…"
                            emptyMessage="No other top-level tasks in this project."
                            testId="attach-subtasks"
                            maxHeight="max-h-64"
                          />
                        </div>

                        <div className="text-xs text-zinc-500 leading-relaxed">
                          These keep everything they already have — status, assignees, ETA, comments and
                          history. Only their position changes: each is renumbered under
                          #{task.task_number || "?"}, and its own subtasks follow it.
                          Only top-level tasks in <span className="font-medium">{project?.title || "this project"}</span> are
                          listed, so nothing can be moved out of its project or under its own subtask.
                        </div>

                        <div className="flex justify-end">
                          <Button
                            onClick={attachSubtasks}
                            disabled={attaching || attachIds.length === 0}
                            data-testid="attach-subtasks-button"
                            className="rounded-sm bg-[#0176D3] hover:bg-[#005FB2]"
                          >
                            {attaching
                              ? "Moving…"
                              : `Move ${attachIds.length || ""} task${attachIds.length === 1 ? "" : "s"} here`.replace("  ", " ")}
                          </Button>
                        </div>
                      </TabsContent>
                    </Tabs>
                  </DialogContent>
                </Dialog>
              )}
            </div>

            {subtasks.length === 0 ? (
              <div className="text-sm text-zinc-500">
                No subtasks. {canManage && "Break this down if it has distinct pieces of work."}
              </div>
            ) : (
              <div className="border border-border rounded-sm divide-y divide-border" data-testid="subtask-list">
                {subtasks.map((st) => (
                  <Link
                    key={st._id}
                    to={`/tasks/${st._id}`}
                    state={{ from: backTo }}
                    data-testid={`subtask-row-${st._id}`}
                    className="flex items-center justify-between gap-3 px-3 py-2 hover:bg-zinc-50 transition-colors"
                  >
                    <div className="min-w-0 flex items-center gap-2">
                      <span className="font-mono text-xs text-zinc-500 tabular-nums shrink-0">
                        {st.task_number || "—"}
                      </span>
                      <span className="text-sm break-words">{st.title}</span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {/* Who it is on, right where it is listed — the reason to
                          break a task down is usually to hand the pieces out. */}
                      <AvatarStack
                        people={(st.assignee_ids || [st.assignee_id])
                          .filter(Boolean)
                          .map((uid) => uById[uid] || { id: uid, name: "Unknown" })}
                        limit={2}
                      />
                      <PriorityBadge priority={st.priority} />
                      <StatusBadge status={st.status} />
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          <div className="border border-border rounded-sm bg-white p-5">
            <div className="trk-uppercase-label mb-3">Assignees</div>
            <div className="space-y-2">
              {canManage ? (
                <div>
                  <Label className="trk-uppercase-label text-[10px]">Primary</Label>
                  <Select value={task.assignee_id || ""} onValueChange={(v) => setPrimaryAssignee(v)}>
                    <SelectTrigger className="rounded-sm mt-1 w-full sm:w-64"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">Unassigned</SelectItem>
                      {users.map((u) => <SelectItem key={u._id} value={u._id}>{u.name || u.email}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="text-sm text-zinc-700">
                  <span className="trk-uppercase-label text-[10px] mr-1">Primary</span>
                  {uMap[task.assignee_id] || "Unassigned"}
                </div>
              )}

              {extraAssigneeIds.length > 0 && (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {extraAssigneeIds.map((uid) => (
                    <span key={uid} className="inline-flex items-center gap-1.5 px-2 py-0.5 text-xs rounded-sm border border-border bg-zinc-50">
                      {uMap[uid] || "Unknown"}
                      {canManage && (
                        <button onClick={() => removeAssignee(uid)} className="text-zinc-400 hover:text-red-600" data-testid={`remove-assignee-${uid}`}>
                          <XIcon size={10} />
                        </button>
                      )}
                    </span>
                  ))}
                </div>
              )}

              {canManage && (
                <div className="flex items-center gap-2 pt-2">
                  <Select value={addAssigneeId} onValueChange={setAddAssigneeId}>
                    <SelectTrigger className="rounded-sm h-8 text-xs w-52" data-testid="add-assignee-select"><SelectValue placeholder="Add teammate…" /></SelectTrigger>
                    <SelectContent>
                      {addableUsers.map((u) => <SelectItem key={u._id} value={u._id}>{u.name || u.email}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <button
                    disabled={!addAssigneeId}
                    onClick={addAssignee}
                    data-testid="add-assignee-button"
                    className="h-8 px-2 text-[10px] font-mono uppercase tracking-wider rounded-sm border border-[#0176D3] bg-[#0176D3] text-white hover:bg-[#005FB2] disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
                  >
                    <PlusIcon size={12} /> Add
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right: comments */}
        <div className="border border-border rounded-sm bg-white p-5 flex flex-col h-fit lg:sticky lg:top-32">
          <div className="trk-uppercase-label mb-3">Activity &amp; Comments {comments.length > 0 && `(${comments.length})`}</div>
          <div className="space-y-3 max-h-[480px] overflow-y-auto pr-1" data-testid="task-comments-list">
            {timeline.length === 0 ? (
              <p className="text-sm text-zinc-500">No activity yet. Be the first to add a comment.</p>
            ) : (
              <PagedList items={timeline} pageSize={20} testIdPrefix="task-timeline">
                {(rows) => (
                  <div className="space-y-3">
              {rows.map(({ kind, item: c }) =>
                kind === "activity" ? (
                  <div key={`a-${c._id}`} className="flex items-start gap-2" data-testid={`task-activity-${c._id}`}>
                    <span className="w-1 h-1 mt-1.5 rounded-full bg-zinc-300 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs text-zinc-500 leading-relaxed">
                        <span className="font-medium text-zinc-600">{c.actor_name}</span> {describeActivity(c)}
                        <span className="font-mono text-[10px] text-zinc-400 ml-1.5">{new Date(c.created_at).toLocaleString()}</span>
                      </p>
                      {/* The reason given at the time — currently only written
                          for a revised-ETA change. Kept on the event rather
                          than posted as a comment, so a later edit to the date
                          cannot detach it from the date it explains. */}
                      {c.note && (
                        <p
                          className="text-xs text-zinc-700 mt-1 pl-2 border-l-2 border-amber-300 whitespace-pre-wrap"
                          data-testid={`task-activity-note-${c._id}`}
                        >
                          {c.note}
                        </p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div key={`c-${c._id}`} className="group pt-1" data-testid={`task-comment-${c._id}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-[#0F172A]">{c.author_name}</span>
                      <span className="text-[10px] font-mono text-zinc-400">{new Date(c.created_at).toLocaleString()}</span>
                    </div>
                    <p className="text-sm text-zinc-700 mt-1 whitespace-pre-wrap">{c.body}</p>
                  </div>
                )
              )}
                  </div>
                )}
              </PagedList>
            )}
          </div>

          {subtaskActivity.length > 0 && (
            <div className="mt-4 pt-4 border-t border-border">
              <div className="trk-uppercase-label mb-3">Subtask activity</div>
              <div className="space-y-3 max-h-[300px] overflow-y-auto pr-1" data-testid="subtask-activity-list">
                <PagedList items={subtaskActivity} pageSize={20} testIdPrefix="subtask-activity">
                  {(rows) => (
                    <div className="space-y-3">
                      {rows.map((sa) => (
                        <div key={`sa-${sa._id}`} className="flex items-start gap-2" data-testid={`subtask-activity-${sa._id}`}>
                          <span className="w-1 h-1 mt-1.5 rounded-full bg-zinc-300 flex-shrink-0" />
                          <p className="text-xs text-zinc-500 leading-relaxed">
                            <span className="font-medium text-zinc-600">{sa.actor_name}</span>{" "}
                            {sa.action === "subtask_added"
                              ? `added subtask "${sa.to_value}"`
                              : `${describeActivity(sa)} on subtask "${sa.subtask_title}"`}
                            <span className="font-mono text-[10px] text-zinc-400 ml-1.5">{new Date(sa.created_at).toLocaleString()}</span>
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </PagedList>
              </div>
            </div>
          )}

          <div className="mt-4 pt-4 border-t border-border space-y-2">
            <Textarea
              value={commentBody}
              onChange={(e) => setCommentBody(e.target.value)}
              placeholder="Add a comment…"
              className="rounded-sm"
              rows={3}
              data-testid="task-comment-input"
            />
            <Button
              onClick={postComment}
              disabled={posting || !commentBody.trim()}
              data-testid="post-task-comment"
              className="rounded-sm bg-[#0A0A0A] hover:bg-black text-white w-full"
            >
              <PaperPlaneTiltIcon size={14} className="mr-1" /> {posting ? "Posting…" : "Comment"}
            </Button>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
